<template>
    <div
        :class="{
            'form__field--error': errorMessage,
            'split-input--new': isNewDesign,
        }"
        class="form__field"
    >
        <label
            v-if="!isNewDesign"
            class="input__label capitalize"
        >
            {{ $t(label) }}
        </label>
        <div
            :class="{ 'input__field--prefixed' : prefix }"
            class="input__field-box"
        >
            <span
                v-if="prefix"
                class="input__prefix"
            >
                {{ prefix }}
            </span>
            <Icon
                v-if="isCheckmarkVisible"
                class="input__field--success"
                :class="successIconClasses"
            />
            <div class="input__multiple-fields-box">
                <div
                    v-if="isNewDesign"
                    class="input__label capitalize"
                >
                    {{ $t(label) }}
                </div>
                <input
                    v-for="(val, index) in values"
                    class="input__field"
                    :placeholder="placeholders[index] || ''"
                    :key="index"
                    :value="val"
                    :ref="'input' + index"
                    :class="{ 'input__field--error' : errorMessage }"
                    v-bind="$attrs"
                    @input="onInput(index, $event)"
                    @blur="onBlur(index)"
                    @focus="onFocus"
                />
            </div>
        </div>
        <div
            v-if="errorMessage"
            class="message message--error"
        >
            <Icon
                size="xs"
                class="icon-warning_triangle"
            />
            <span>
                {{ errorMessage }}
            </span>
        </div>
    </div>
</template>

<script>
    import BaseInput from './BaseInput';

    export default {
        name: 'SplitInput',
        mixins: [BaseInput],
        emits: [
            'input',
            'blur',
            'valueChanged',
        ],
        inheritAttrs: false,
        props: {
            label: {
                type: String,
                required: false,
                default: '',
            },
            errorMessage: {
                type: String,
                required: false,
                default: null,
            },
            prefix: {
                type: String,
                required: false,
                default: null,
            },
            placeholder: {
                type: String,
                required: true,
            },
            joinCharacter: {
                type: String,
                required: false,
                default: '',
            },
            isJumpingAllowed: {
                type: Boolean,
                required: false,
                default: true,
            },
            value: {
                type: String,
                default: '',
            },
            normalizeOnBlur: {
                type: Function,
                default: null,
            },
            isCheckmarkEnabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            isPendingValidation: {
                type: Boolean,
                required: false,
                default: false,
            },
            isNewDesign: {
                type: Boolean,
                required: false,
                default: false,
            },
            shouldEmitOnlyAfterFullTouched: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                // field is needed because user can click on input without writing anything
                // but we want to show him an error if field is required
                isDirty: false,
                values: [],
                isTouched: false,
                isFocused: false,
                isLastFieldTouched: false,
            };
        },
        created() {
            this.placeholders = this.placeholder.split(this.joinCharacter);
            this.nFields = this.placeholders.length;
            if (this.value) {
                this.values = this.value.split(this.joinCharacter);
            } else {
                this.values = new Array(this.nFields).fill('');
            }
        },
        computed: {
            joinedValue() {
                return this.values.join(this.joinCharacter);
            },
            isFilled() {
                return this.values.filter((value) => value).length === this.values.length;
            },
            isCheckmarkVisible() {
                return !this.errorMessage && this.isFilled && this.isCheckmarkEnabled && this.isTouched && !this.isFocused && !this.isPendingValidation;
            },
            successIconClasses() {
                return this.isNewDesign ? 'icon-check_circle_filled' : 'icon-check';
            },
        },
        watch: {
            value(newVal) {
                if (!newVal || !this.joinCharacter) {
                    return;
                }
                this.values = newVal.split(this.joinCharacter);

                // Support outside input but keep emit on last input
                this.isTouched = this.values.length === this.nFields;
                this.isLastFieldTouched = this.values.length === this.nFields;
            },
        },
        methods: {
            focusInput(index) {
                this.$refs[`input${index}`][0].focus();
            },
            blurInput(index) {
                this.$refs[`input${index}`][0].blur();
            },
            onInput(index, event) {
                // TODO for reuse refactor this method
                this.isTouched = true;
                this.isLastFieldTouched = this.isLastFieldTouched || index === this.placeholders.length - 1;
                this.isDirty = true;
                let value = this.getValue(event).substr(0, this.placeholders[index].length);

                this.$set(this.values, index, value);
                this.$emit('input', this.joinedValue);
                this.$emit('valueChanged', this.joinedValue, this.name);

                // eslint-disable-next-line sonarjs/no-collapsible-if
                if (this.isJumpingAllowed) {
                    if (this.placeholders[index] && value.length >= this.placeholders[index].length) {
                        if (index < this.nFields - 1) {
                            this.focusInput(index + 1);
                        } else {
                            this.blurInput(index);
                        }
                        if (this.normalizeOnBlur) {
                            const values = [...this.values];
                            values[index] = value;
                            value = this.formatValueAtIndex(index, values);
                        }
                    }
                }
            },
            onBlur(index) {
                if (!this.isLastFieldTouched && this.shouldEmitOnlyAfterFullTouched) {
                    return;
                }

                if (this.normalizeOnBlur && !!this.values[index]) {
                    const formatedValue = this.formatValueAtIndex(index, this.values);
                    this.$set(this.values, index, formatedValue);
                    this.$emit('input', this.joinedValue);
                    this.$emit('valueChanged', this.joinedValue, this.name);
                }
                this.$emit('blur', this.name, this.joinedValue, this.isDirty);
                this.isFocused = false;
            },
            onFocus() {
                this.isFocused = true;
            },
            formatValueAtIndex(index, values) {
                const joinedValue = this.normalizeOnBlur(values.join(this.joinCharacter));
                return joinedValue.split(this.joinCharacter)[index];
            },
        },
    };
</script>
